import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { Dialog } from "@material-tailwind/react";
import DeleteModal from "components/DeleteModal/DeleteModal";
import DynamicTable from "components/Tables/DynamicTable";
import DashboardMenu from "containers/Admin/DashboardPage/DashboardMenu";
import { useAppDispatch, useAppSelector, useDebounce } from "hooks/hooks";
import { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { FaGlassCheers } from "react-icons/fa";
import { adminGetEvents, deleteEvent } from "redux/features/Admin/Events/eventSlice";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Input from "shared/Input/Input";
import AddEventPage from "./AddEventPage"; // Import AddEventPage
import UpdateEventPage from "./UpdateEventPage"; // Import UpdateEventPage
import Loading from "components/Loader/Loading";

export interface ListEventsPageProps {
  className?: string;
}

export interface Event {
  designation: string;
  id: number;
  images: string[];
}

interface ModalState {
  isDeleteModalOpen: boolean;
  isAddModalOpen: boolean;
  isUpdateModalOpen: boolean;
  selectedItem: Event | null;
  loading: boolean;
}

const ListEventsPage: FC<ListEventsPageProps> = ({ className = "" }) => {
  const dispatch = useAppDispatch();
  const {
    events: eventsList,
    totalCount,
    totalPages,
    currentPage,
    loading,
  } = useAppSelector((state) => ({
    events: state.featureEvent?.allEvents?.events || [],
    totalCount: state.featureEvent?.allEvents?.totalCount || 0,
    totalPages: state.featureEvent?.allEvents?.totalPages || 0,
    currentPage: state.featureEvent?.allEvents?.currentPage || 1,
    loading: state.featureEvent?.loading || "idle",
  }));

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [modalState, setModalState] = useState<ModalState>({
    loading: false,
    isDeleteModalOpen: false,
    isAddModalOpen: false,
    isUpdateModalOpen: false,
    selectedItem: null,
  });

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  useEffect(() => {
    const fetchData = async () => {
      setIsSearchLoading(true);
      await dispatch(adminGetEvents({ page, searchTerm: debouncedSearchTerm }));
      setIsSearchLoading(false);
    };
    fetchData();
  }, [dispatch, page, debouncedSearchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const handlePageChange = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handleAddModalOpen = () => {
    setModalState({ ...modalState, isAddModalOpen: true });
  };

  const handleUpdateModalOpen = (event: Event) => {
    setModalState({ ...modalState, isUpdateModalOpen: true, selectedItem: event });
  };

  const handleDeleteModalOpen = (event: Event) => {
    setModalState({ ...modalState, selectedItem: event, isDeleteModalOpen: true });
  };

  const handleModalClose = () => {
    setModalState({
      ...modalState,
      isDeleteModalOpen: false,
      isAddModalOpen: false,
      isUpdateModalOpen: false,
      selectedItem: null,
    });
  };

  const handleDeleteConfirm = async () => {
    if (modalState.selectedItem) {
      setModalState((prev) => ({ ...prev, loading: true }));
      await dispatch(deleteEvent(modalState.selectedItem.id));
      setModalState({
        ...modalState,
        isDeleteModalOpen: false,
        loading: false,
        selectedItem: null,
      });
      await dispatch(adminGetEvents({ page, searchTerm: debouncedSearchTerm }));
    }
  };

  const handleTableAction = (action: string, item: Event) => {
    if (action === "delete") {
      handleDeleteModalOpen(item);
    } else if (action === "edit") {
      handleUpdateModalOpen(item);
    }
  };

  const columns = [
    {
      key: "image",
      label: "Image",
      render: (item: Event) => (
        <div className="flex items-center justify-center w-16 h-16">
          {item.images && item.images[0] ? (
            <img
              src={`${process.env.REACT_APP_CLOUD_FRONT}${item.images[0]}`}
              alt={item.designation}
              className="object-cover w-full h-full rounded"
            />
          ) : (
            <span className="text-center align-middle">N/A</span>
          )}
        </div>
      ),
    },
    {
      key: "designation",
      label: "Designation",
      render: (item: Event) => <span className="align-middle">{item.designation}</span>,
    },
    {
      key: "actions",
      label: "Actions",
    },
  ];

  return (
    <div className="min-h-screen nc-AccountPage bg-gray-50 dark:bg-gray-900" data-nc-id="AccountPage">
      <Helmet>
        <title>EventToBee | Location d'espaces : salles, lofts, jardins & boutiques – Paris & Île-de-France</title>
      </Helmet>
      <DashboardMenu>
        <div className="container px-4 py-8 mx-auto">
          <div className="flex flex-col justify-between gap-4 mb-10 sm:flex-row sm:items-center">
            <div className="flex items-center space-x-3">
              <FaGlassCheers className="text-3xl text-blue-500 dark:text-blue-400" aria-hidden="true" />
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                Liste Des Événements
              </h2>
            </div>
            <div className="flex items-center w-full space-x-4 sm:w-auto">
              <ButtonPrimary onClick={handleAddModalOpen} className="btn-primary-gradient" sizeClass="px-4 py-2">
                Ajouter événement
              </ButtonPrimary>
              <div className="relative w-full max-w-lg">
                <Input
                  type="text"
                  placeholder="Rechercher par désignation..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full py-3 pl-11 pr-10 text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-xl shadow-sm dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:shadow-md transition-all duration-300 ease-in-out focus:scale-[1.01]"
                  aria-label="Search events"
                />
                <MagnifyingGlassIcon
                  className={`absolute w-5 h-5 top-1/2 transform -translate-y-1/2 left-3 transition-colors ${
                    searchTerm ? "text-blue-500 dark:text-blue-400" : "text-gray-400 dark:text-gray-500"
                  }`}
                />
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute w-5 h-5 p-0.5 top-1/2 transform -translate-y-1/2 right-3 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                    aria-label="Clear search"
                  >
                    <XMarkIcon />
                  </button>
                )}
              </div>
            </div>
          </div>

          {isSearchLoading && <Loading />}

          {loading === "pending" && !isSearchLoading && <Loading />}

          {loading !== "pending" && (
            <>
              {/* Delete Modal */}
              {modalState.isDeleteModalOpen && (
                <DeleteModal
                  open={modalState.isDeleteModalOpen}
                  handleClose={handleModalClose}
                  handleDelete={handleDeleteConfirm}
                  category={{
                    id: modalState.selectedItem?.id || 0,
                    designation: modalState.selectedItem?.designation || "",
                  }}
                />
              )}

              {/* Add Event Modal */}
              <Dialog
                size="sm"
                open={modalState.isAddModalOpen}
                handler={handleModalClose}
                className="w-full max-w-md mx-auto bg-white shadow-2xl rounded-2xl dark:bg-gray-900"
              >
                <AddEventPage
                  className="modal-content"
                  onClose={() => {
                    handleModalClose();
                    dispatch(adminGetEvents({ page, searchTerm: debouncedSearchTerm }));
                  }}
                />
              </Dialog>

              {/* Update Event Modal */}
              <Dialog
                size="sm"
                open={modalState.isUpdateModalOpen}
                handler={handleModalClose}
                className="w-full max-w-md mx-auto bg-white shadow-2xl rounded-2xl dark:bg-gray-900"
              >
                {modalState.selectedItem && (
                  <UpdateEventPage
                    className="modal-content"
                    eventId={modalState.selectedItem.id.toString()}
                    onClose={() => {
                      handleModalClose();
                      dispatch(adminGetEvents({ page, searchTerm: debouncedSearchTerm }));
                    }}
                  />
                )}
              </Dialog>

              <DynamicTable
                columns={columns}
                data={eventsList}
                totalItems={totalCount || 0}
                currentPage={currentPage || 1}
                onPageChange={handlePageChange}
                onAction={handleTableAction}
                totalPages={totalPages}
                availableActions={["edit", "delete"]}
              />
            </>
          )}
        </div>
      </DashboardMenu>
    </div>
  );
};

export default ListEventsPage;
